import { createDocumentFromSnapshot, readDocument } from '@/persistence/local'
import { deviceCanvasRequest } from './canvasRequest'
import { loadPage, registeredPageById, registeredPages } from './pageRegistry'
import type { PageCard } from './pageTypes'

/*
 * Page versions (docs/pages.md, Versions). Related pages are one page with versions: a newer page
 * names the page it is a version of (`PageDefinition.versionOf`, the family's first page), and the
 * old page is left as it is (sketchbook rule: a version is a new folder and brief, never an edit to
 * an old one). The home screen shows one card per family, the newest open version's; inside the
 * page the owner flips between versions in the settings panel (`VersionSwitch`).
 *
 * Every version keeps its own world, shell, brief and documents: a switch opens that version's own
 * document. Documents flipped between in one sitting are linked (localStorage, per document), so
 * flipping back finds the child's drawing in each version as it was left.
 */

/** The family's first page: `versionOf` followed to the end (a page without it is its own root). */
export function familyRootOf(page: PageCard): PageCard {
  let root = page
  const seen = new Set<string>()
  while (root.versionOf && !seen.has(root.id)) {
    seen.add(root.id)
    const next = registeredPageById(root.versionOf)
    if (!next) break
    root = next
  }
  return root
}

/** Every version of the page's family, oldest first (by number); just the page when it has none. */
export function pageVersionsOf(page: PageCard, pages: readonly PageCard[] = registeredPages()): readonly PageCard[] {
  const root = familyRootOf(page).id
  const versions = pages.filter((p) => familyRootOf(p).id === root)
  return versions.length > 0 ? [...versions].sort((a, b) => a.number - b.number) : [page]
}

/** One home card: a family, the version it opens (the newest open one) and the name it goes by (the first's). */
export type PageFamily = { root: PageCard; versions: readonly PageCard[]; newest: PageCard }

/**
 * The families for the home screen, newest first (by their newest version). A family whose every
 * version is archived is left off (its documents still open from your worlds).
 */
export function pageFamilies(pages: readonly PageCard[] = registeredPages()): PageFamily[] {
  const byRoot = new Map<string, PageCard[]>()
  for (const p of pages) {
    const root = familyRootOf(p).id
    const list = byRoot.get(root)
    if (list) list.push(p)
    else byRoot.set(root, [p])
  }
  const families: PageFamily[] = []
  for (const list of byRoot.values()) {
    const versions = [...list].sort((a, b) => a.number - b.number)
    const open = versions.filter((p) => p.status !== 'archived')
    if (open.length === 0) continue
    families.push({ root: familyRootOf(versions[0]), versions, newest: open[open.length - 1] })
  }
  return families.sort((a, b) => b.newest.number - a.newest.number)
}

/**
 * Create a fresh document for a page (its generator's world, made for this screen: `canvas`) and
 * return its id (route `/local/<id>`). Loads the page's body first if it is not loaded yet.
 */
export async function newPageDocument(card: PageCard): Promise<string> {
  const page = await loadPage(card.id)
  if (!page) throw new Error(`newPageDocument: no page "${card.id}"`)
  const { base, meta } = page.createDocument(deviceCanvasRequest())
  return createDocumentFromSnapshot({ base, meta, events: [], name: `${String(page.number).padStart(3, '0')} ${page.title}` })
}

/** Documents of one sitting, one per version: page id to document id, stored under each member. */
type VersionLinks = Record<string, string>

const LINKS_KEY = (docId: string) => `huebo:page-versions:${docId}`

function readLinks(docId: string): VersionLinks {
  try {
    const raw = localStorage.getItem(LINKS_KEY(docId))
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? parsed as VersionLinks : {}
  } catch {
    return {}
  }
}

function writeLinks(links: VersionLinks): void {
  const text = JSON.stringify(links)
  for (const docId of Object.values(links)) {
    try { localStorage.setItem(LINKS_KEY(docId), text) } catch { /* private mode: flips start fresh */ }
  }
}

/**
 * The document to open for `target` when flipping from document `fromDoc` of page `from`: the one
 * this sitting already has for that version (still saved: an empty world is thrown away when left),
 * else a fresh one, linked to the others so flipping back returns to each drawing.
 */
export async function documentForVersion(fromDoc: string, from: PageCard, target: PageCard): Promise<string> {
  const links: VersionLinks = { ...readLinks(fromDoc), [from.id]: fromDoc }
  const known = links[target.id]
  if (known && known !== fromDoc && readDocument(known)) return known
  const id = await newPageDocument(target)
  writeLinks({ ...links, [target.id]: id })
  return id
}
